import Phaser from 'phaser'
import { GAME_HEIGHT, GAME_WIDTH } from '../config'
import { ENEMY_SPRITES } from '../sprites'
import {
  advancePathFollower,
  buildDemoPath,
  DEMO_TOWER_MIN_SEPARATION,
  getDemoControlPositions,
  getDemoLayout,
  getDemoPlacementArea,
  getDemoTowerRange,
  isInsideDemoPlacementArea,
  type DemoPoint,
  type PathFollowerState,
} from './demoModel'
import { HoneyTowerV2View, HONEY_V2_ASSETS } from '../v2/HoneyTowerV2View'
import { HONEY_TOWER_DISPLAY_SCALE } from '../v2/honeyRenderContract'
import { changeTowerLevel, type TowerLane } from '../v2/towerModel'
import {
  HONEY_COMBAT,
  advanceHoneyCooldown,
  applyHoneyHit,
  borrowHoneyFish,
  createHoneyCooldown,
  createHoneyFishLoans,
  returnHoneyFish,
  selectHoneyTarget,
  tickHoneyEnemy,
  type HoneyCooldownState,
  type HoneyFishLoanState,
} from '../v2/combatModel'
import {
  createHoneyProjectile,
  stepHoneyProjectile,
  type HoneyProjectileState,
} from '../v2/projectileModel'
import {
  beginDemoPlacement,
  cancelDemoPlacement,
  confirmDemoPlacement,
  createDemoPlacementState,
  lockDemoPlacement,
  moveDemoPlacement,
  type DemoPlacementState,
} from './placementModel'

interface DemoEnemy {
  id: number
  sprite: Phaser.GameObjects.Image
  follower: PathFollowerState
  speed: number
  hp: number
  alive: boolean
  slowRemainingMs: number
}

interface DemoTowerCombat {
  cooldown: HoneyCooldownState
  loans: HoneyFishLoanState
  pendingTarget: { id: number; x: number; y: number } | null
}

interface DemoProjectile {
  sprite: Phaser.GameObjects.Sprite
  state: HoneyProjectileState
  owner: HoneyTowerV2View
  fishIndex: 0 | 1
  ageMs: number
}

interface DemoImpact {
  sprite: Phaser.GameObjects.Sprite
  owner: HoneyTowerV2View
  fishIndex: 0 | 1
  ageMs: number
}

export class DemoScene extends Phaser.Scene {
  private readonly towers: HoneyTowerV2View[] = []
  private readonly enemies: DemoEnemy[] = []
  private readonly towerCombat = new Map<HoneyTowerV2View, DemoTowerCombat>()
  private readonly projectiles: DemoProjectile[] = []
  private readonly impacts: DemoImpact[] = []
  private nextEnemyId = 1
  private roadPath: DemoPoint[] = []
  private selectedTower: HoneyTowerV2View | null = null
  private placement: DemoPlacementState = createDemoPlacementState()
  private placementGhost: Phaser.GameObjects.Container | null = null
  private confirmButton!: Phaser.GameObjects.Container
  private confirmButtonBg!: Phaser.GameObjects.Rectangle
  private cancelButton!: Phaser.GameObjects.Container
  private isMobile = false
  private mapInputTop = 90
  private statusText!: Phaser.GameObjects.Text
  private bearLevelText!: Phaser.GameObjects.Text
  private spaLevelText!: Phaser.GameObjects.Text
  private placeButton!: Phaser.GameObjects.Rectangle
  private placeButtonLabel!: Phaser.GameObjects.Text
  private removeButton!: Phaser.GameObjects.Rectangle
  private removeButtonLabel!: Phaser.GameObjects.Text
  private loadErrors: string[] = []

  constructor() {
    super({ key: 'DemoScene' })
  }

  preload(): void {
    this.loadErrors = []
    this.load.off('loaderror', this.handleLoadError, this)
    this.load.on('loaderror', this.handleLoadError, this)
    for (const asset of [HONEY_V2_ASSETS.composite, HONEY_V2_ASSETS.waterMask, HONEY_V2_ASSETS.waterSurface]) {
      if (!this.textures.exists(asset.key)) this.load.image(asset.key, asset.path)
    }
    for (const asset of [HONEY_V2_ASSETS.fish, HONEY_V2_ASSETS.throwBody, HONEY_V2_ASSETS.impact]) {
      if (!this.textures.exists(asset.key)) {
        this.load.spritesheet(asset.key, asset.path, {
          frameWidth: asset.frameWidth,
          frameHeight: asset.frameHeight,
        })
      }
    }
  }

  private handleLoadError(file: Phaser.Loader.File): void {
    this.loadErrors.push(file.key)
  }

  create(): void {
    this.towers.length = 0
    this.enemies.length = 0
    this.towerCombat.clear()
    this.projectiles.length = 0
    this.impacts.length = 0
    this.nextEnemyId = 1
    this.selectedTower = null
    this.placement = createDemoPlacementState()
    this.placementGhost = null
    const layout = getDemoLayout(GAME_WIDTH)
    this.isMobile = layout.isMobile
    this.mapInputTop = layout.topInset

    for (const key of [
      HONEY_V2_ASSETS.composite.key,
      HONEY_V2_ASSETS.waterMask.key,
      HONEY_V2_ASSETS.waterSurface.key,
      HONEY_V2_ASSETS.fish.key,
      HONEY_V2_ASSETS.throwBody.key,
      HONEY_V2_ASSETS.impact.key,
    ]) {
      if (this.textures.exists(key)) this.textures.get(key).setFilter(Phaser.Textures.FilterMode.NEAREST)
    }

    this.drawDemoMap()
    this.createControls()
    this.createPlacementButtons()
    this.input.on('pointerdown', this.onMapPointerDown, this)
    this.input.on('pointermove', this.onMapPointerMove, this)
    this.input.keyboard?.on('keydown-ESC', this.onPlacementEscape, this)
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.teardown, this)

    if (this.loadErrors.length > 0) {
      this.setStatus(`ASSET ERROR: ${this.loadErrors.join(', ')}`)
    } else {
      this.setStatus('Place a Honey Bear inside the marked demo area. Spawn a snowman to test sticky-fish combat.')
    }
  }

  private drawDemoMap(): void {
    const cx = GAME_WIDTH / 2
    const cy = GAME_HEIGHT / 2
    this.add.rectangle(cx, cy, GAME_WIDTH, GAME_HEIGHT, 0xdce8f0).setDepth(-10)

    // Deterministic snow texture: no random placement means screenshots are stable.
    const snow = this.add.graphics().setDepth(-9)
    snow.fillStyle(0xc7d9e8, 0.55)
    for (let y = this.mapInputTop + 15; y < GAME_HEIGHT - 25; y += 34) {
      for (let x = 18 + ((y / 34) % 2) * 14; x < GAME_WIDTH; x += 46) {
        snow.fillCircle(x, y, 2)
      }
    }

    this.roadPath = buildDemoPath(GAME_WIDTH, GAME_HEIGHT)
    const road = this.add.graphics().setDepth(0)
    this.strokeRoad(road, 32.5, 0x6b4631)
    this.strokeRoad(road, 25, 0xb58a63)
    this.strokeRoad(road, 2.5, 0xd9b58c, 0.45)

    const area = getDemoPlacementArea(GAME_WIDTH, GAME_HEIGHT)
    this.add.ellipse(area.x, area.y, area.radiusX * 2, area.radiusY * 2, 0xfff8dc, 0.36)
      .setStrokeStyle(2, 0xd4a72c, 0.9)
      .setDepth(1)
  }

  private strokeRoad(graphics: Phaser.GameObjects.Graphics, width: number, color: number, alpha = 1): void {
    graphics.lineStyle(width, color, alpha)
    graphics.beginPath()
    graphics.moveTo(this.roadPath[0][0], this.roadPath[0][1])
    for (let i = 1; i < this.roadPath.length; i++) {
      graphics.lineTo(this.roadPath[i][0], this.roadPath[i][1])
    }
    graphics.strokePath()
    graphics.fillStyle(color, alpha)
    for (const [x, y] of this.roadPath) graphics.fillCircle(x, y, width / 2)
  }

  private createControls(): void {
    const layout = getDemoLayout(GAME_WIDTH)
    const positions = getDemoControlPositions(GAME_WIDTH)
    const y = layout.controlY
    const stepWidth = positions.stepWidth

    this.add.rectangle(GAME_WIDTH / 2, y, GAME_WIDTH - 18, layout.controlHeight, 0x3e2723, 0.94)
      .setStrokeStyle(2, 0x1d0f04)
      .setDepth(5000)

    this.addButton(positions.menu, y, positions.menuWidth, 'MENU', () => this.scene.start('BootScene'))
    const place = this.addButton(positions.place, y, positions.placeWidth, 'PLACE BEAR', () => this.togglePlacement())
    this.placeButton = place.background
    this.placeButtonLabel = place.label
    this.addButton(positions.spawn, y, positions.spawnWidth, 'SPAWN SNOWMAN', () => this.spawnSnowman())

    this.add.text(positions.bearLabel, y, 'BEAR', this.controlTextStyle()).setOrigin(0.5).setDepth(5001)
    this.addButton(positions.bearMinus, y, stepWidth, '−', () => this.stepSelectedLevel('bear', -1))
    this.bearLevelText = this.add.text(positions.bearValue, y, '—', this.controlTextStyle()).setOrigin(0.5).setDepth(5001)
    this.addButton(positions.bearPlus, y, stepWidth, '+', () => this.stepSelectedLevel('bear', 1))

    this.add.text(positions.spaLabel, y, 'SPA', this.controlTextStyle()).setOrigin(0.5).setDepth(5001)
    this.addButton(positions.spaMinus, y, stepWidth, '−', () => this.stepSelectedLevel('spa', -1))
    this.spaLevelText = this.add.text(positions.spaValue, y, '—', this.controlTextStyle()).setOrigin(0.5).setDepth(5001)
    this.addButton(positions.spaPlus, y, stepWidth, '+', () => this.stepSelectedLevel('spa', 1))

    const remove = this.addButton(positions.remove, y, positions.removeWidth, 'REMOVE', () => this.removeSelectedTower())
    this.removeButton = remove.background
    this.removeButtonLabel = remove.label
    this.add.text(positions.styleLabel, y, 'SNES WARM', {
      ...this.controlTextStyle(), color: '#ffd54f', fontSize: this.isMobile ? '16px' : '11px',
    }).setOrigin(0.5).setDepth(5001)

    this.statusText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 20, '', {
      fontFamily: 'monospace', fontSize: this.isMobile ? '16px' : '12px', color: '#3e2723',
      backgroundColor: '#f7fbffdd', padding: { x: 8, y: 4 },
      align: 'center',
    }).setOrigin(0.5).setDepth(5000)
    this.refreshLevelControls()
  }

  private controlTextStyle(): Phaser.Types.GameObjects.Text.TextStyle {
    return {
      fontFamily: 'monospace',
      fontSize: this.isMobile ? '17px' : '13px',
      color: '#ffffff',
      fontStyle: 'bold',
    }
  }

  private addButton(x: number, y: number, width: number, label: string, action: () => void): {
    background: Phaser.GameObjects.Rectangle
    label: Phaser.GameObjects.Text
  } {
    const background = this.add.rectangle(x, y, width, this.isMobile ? 46 : 34, 0x795548)
      .setStrokeStyle(1, 0xc69c6d)
      .setInteractive({ useHandCursor: true })
      .setDepth(5001)
    const text = this.add.text(x, y, label, {
      fontFamily: 'monospace', fontSize: this.isMobile ? '15px' : '11px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(5002)
    background.on('pointerover', () => background.setFillStyle(0x9e6b4b))
    background.on('pointerout', () => background.setFillStyle(
      this.placement.phase !== 'idle' && label === 'PLACE BEAR' ? 0xad1457 : 0x795548,
    ))
    background.on('pointerdown', action)
    return { background, label: text }
  }

  private createPlacementButtons(): void {
    const y = this.isMobile ? GAME_HEIGHT - 92 : GAME_HEIGHT - 70
    const size = this.isMobile ? 58 : 50

    this.confirmButtonBg = this.add.rectangle(0, 0, size, size, 0x78909c)
      .setStrokeStyle(2, 0x455a64)
    const confirmText = this.add.text(0, 0, '✓', {
      fontFamily: 'monospace', fontSize: this.isMobile ? '34px' : '28px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5)
    this.confirmButton = this.add.container(GAME_WIDTH - 60, y, [this.confirmButtonBg, confirmText])
      .setDepth(6000)
      .setVisible(false)
    this.confirmButtonBg.on('pointerdown', () => this.confirmPlacement())

    const cancelBg = this.add.rectangle(0, 0, size, size, 0xef5350)
      .setStrokeStyle(2, 0xc62828)
      .setInteractive({ useHandCursor: true })
    const cancelText = this.add.text(0, 0, '✕', {
      fontFamily: 'monospace', fontSize: this.isMobile ? '34px' : '28px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5)
    this.cancelButton = this.add.container(GAME_WIDTH - 120, y, [cancelBg, cancelText])
      .setDepth(6000)
      .setVisible(false)
    cancelBg.on('pointerdown', () => this.cancelPlacement('Placement cancelled.'))
  }

  private refreshPlacementUi(): void {
    const active = this.placement.phase !== 'idle'
    const pending = this.placement.phase === 'pending'
    this.placeButton.setFillStyle(active ? 0xad1457 : 0x795548)
    this.placeButtonLabel.setText(pending ? '✓ OR ✕' : active ? 'CLICK MAP' : 'PLACE BEAR')
    this.confirmButton.setVisible(active)
    this.cancelButton.setVisible(active)
    this.confirmButtonBg.setFillStyle(pending ? 0x4caf50 : 0x78909c)
    this.confirmButtonBg.setStrokeStyle(2, pending ? 0x388e3c : 0x455a64)
    if (pending) this.confirmButtonBg.setInteractive({ useHandCursor: true })
    else this.confirmButtonBg.disableInteractive()
  }

  private togglePlacement(): void {
    if (this.placement.phase !== 'idle') {
      this.cancelPlacement('Placement cancelled.')
      return
    }
    this.selectTower(null)
    this.placement = beginDemoPlacement(this.placement)
    this.refreshPlacementUi()
    this.setStatus('Move inside the center to preview range, click to lock, then use ✓ or ✕.')
  }

  private isValidPlacement(x: number, y: number): boolean {
    return isInsideDemoPlacementArea(x, y, GAME_WIDTH, GAME_HEIGHT)
      && !this.towers.some(tower => (
        Phaser.Math.Distance.Between(x, y, tower.x, tower.y) < DEMO_TOWER_MIN_SEPARATION
      ))
  }

  private onMapPointerMove(pointer: Phaser.Input.Pointer): void {
    if (this.placement.phase !== 'aiming' || pointer.y < this.mapInputTop) return
    const valid = this.isValidPlacement(pointer.x, pointer.y)
    this.placement = moveDemoPlacement(this.placement, { x: pointer.x, y: pointer.y }, valid)
    this.rebuildPlacementGhost()
  }

  private onMapPointerDown(pointer: Phaser.Input.Pointer): void {
    if (this.placement.phase !== 'aiming' || pointer.y < this.mapInputTop) return
    const valid = this.isValidPlacement(pointer.x, pointer.y)
    this.placement = moveDemoPlacement(this.placement, { x: pointer.x, y: pointer.y }, valid)
    if (!valid) {
      this.rebuildPlacementGhost()
      this.setStatus('Placement must be inside the marked center and clear of other towers.')
      return
    }
    this.placement = lockDemoPlacement(this.placement)
    this.rebuildPlacementGhost()
    this.refreshPlacementUi()
    this.setStatus('Position locked. Use ✓ to approve or ✕ to cancel.')
  }

  private rebuildPlacementGhost(): void {
    this.destroyPlacementGhost()
    if (!this.placement.position || this.placement.phase === 'idle') return

    const valid = this.placement.valid
    const range = getDemoTowerRange(GAME_WIDTH) / HONEY_TOWER_DISPLAY_SCALE
    const ghost = this.add.container(this.placement.position.x, this.placement.position.y)
      .setScale(HONEY_TOWER_DISPLAY_SCALE)
      .setDepth(Math.round(this.placement.position.y) + 1)
      .setAlpha(valid ? 0.72 : 0.42)
    const rangeGraphics = this.add.graphics()
    rangeGraphics.fillStyle(valid ? 0x66bb6a : 0xef5350, 0.15)
    rangeGraphics.lineStyle(2 / HONEY_TOWER_DISPLAY_SCALE, valid ? 0x388e3c : 0xc62828, 0.7)
    rangeGraphics.fillCircle(0, 0, range)
    rangeGraphics.strokeCircle(0, 0, range)
    const bear = this.add.image(0, 0, HONEY_V2_ASSETS.composite.key)
      .setOrigin(0.5, 122 / 128)
      .setTint(valid ? 0xffffff : 0xff8a80)
    ghost.add([rangeGraphics, bear])
    this.placementGhost = ghost
  }

  private destroyPlacementGhost(): void {
    this.placementGhost?.destroy()
    this.placementGhost = null
  }

  private cancelPlacement(message?: string): void {
    this.placement = cancelDemoPlacement(this.placement)
    this.destroyPlacementGhost()
    this.refreshPlacementUi()
    if (message) this.setStatus(message)
  }

  private confirmPlacement(): void {
    const result = confirmDemoPlacement(this.placement)
    if (!result.placement) {
      this.setStatus('Click a valid position before approving placement.')
      return
    }

    this.placement = result.state
    this.destroyPlacementGhost()
    this.refreshPlacementUi()
    const tower = new HoneyTowerV2View(this, result.placement.x, result.placement.y)
    tower.onSelect(() => {
      this.cancelPlacement()
      this.selectTower(tower)
    })
    this.towers.push(tower)
    this.towerCombat.set(tower, {
      cooldown: createHoneyCooldown(),
      loans: createHoneyFishLoans(),
      pendingTarget: null,
    })
    this.selectTower(tower)
    this.setStatus('Honey Bear placed. Spawn a snowman: three sticky fish hits will defeat it.')
  }

  private onPlacementEscape(): void {
    if (this.placement.phase !== 'idle') this.cancelPlacement('Placement cancelled.')
  }

  private selectTower(tower: HoneyTowerV2View | null): void {
    this.selectedTower = tower
    for (const candidate of this.towers) candidate.setSelected(candidate === tower)
    this.refreshLevelControls()
  }

  private stepSelectedLevel(lane: TowerLane, delta: number): void {
    if (!this.selectedTower) {
      this.setStatus('Place or select a Honey Bear first.')
      return
    }
    const levels = changeTowerLevel(this.selectedTower.getLevels(), lane, delta)
    this.selectedTower.setLevels(levels)
    this.refreshLevelControls()
    const fallback = levels.bearLevel !== 0 || levels.spaLevel !== 0
    this.setStatus(fallback
      ? `Selected B${levels.bearLevel}/S${levels.spaLevel}. Upgrade art pending; showing labeled L0 placeholder.`
      : 'Selected B0/S0 approved SNES Warm base art.')
  }

  private refreshLevelControls(): void {
    const levels = this.selectedTower?.getLevels()
    this.bearLevelText?.setText(levels ? String(levels.bearLevel) : '—')
    this.spaLevelText?.setText(levels ? String(levels.spaLevel) : '—')
    this.removeButton.setVisible(Boolean(this.selectedTower))
    this.removeButtonLabel.setVisible(Boolean(this.selectedTower))
    if (this.selectedTower) this.removeButton.setInteractive({ useHandCursor: true })
    else this.removeButton.disableInteractive()
  }

  private removeSelectedTower(): void {
    if (!this.selectedTower) {
      this.setStatus('No Honey Bear selected.')
      return
    }
    const index = this.towers.indexOf(this.selectedTower)
    this.towerCombat.delete(this.selectedTower)
    this.selectedTower.destroy()
    if (index >= 0) this.towers.splice(index, 1)
    this.selectTower(this.towers.at(-1) ?? null)
    this.setStatus('Selected Honey Bear removed. No money involved.')
  }

  private spawnSnowman(): void {
    const [x, y] = this.roadPath[0]
    const sprite = this.add.image(x, y, ENEMY_SPRITES.BASIC)
      .setScale(this.isMobile ? 0.04 : 0.05)
      .setDepth(Math.round(y) + 2)
    this.enemies.push({
      id: this.nextEnemyId++,
      sprite,
      speed: 58,
      hp: 9,
      alive: true,
      slowRemainingMs: 0,
      follower: { pathIndex: 0, x, y, complete: false },
    })
    this.setStatus('Snowman spawned with 9 HP. Honey Bear deals 3 damage and 40% slow per sticky fish.')
  }

  private setStatus(message: string): void {
    this.statusText?.setText(message)
  }

  update(_time: number, delta: number): void {
    for (const enemy of this.enemies) {
      const ticked = tickHoneyEnemy(enemy, delta)
      enemy.slowRemainingMs = ticked.slowRemainingMs
      const speedScale = enemy.slowRemainingMs > 0 ? 1 - HONEY_COMBAT.slowAmount : 1
      enemy.follower = advancePathFollower(enemy.follower, this.roadPath, enemy.speed * speedScale * delta / 1000)
      enemy.sprite.setPosition(enemy.follower.x, enemy.follower.y)
      enemy.sprite.setDepth(Math.round(enemy.follower.y) + 2)
      enemy.sprite.setTint(enemy.slowRemainingMs > 0 ? 0xffc04d : 0xffffff)
      if (enemy.follower.complete) {
        enemy.alive = false
        enemy.sprite.destroy()
      }
    }

    for (const tower of this.towers) {
      tower.updateIdle(delta)
      tower.setDepth(Math.round(tower.y))
      this.updateTowerCombat(tower, delta)
    }
    this.updateProjectiles(delta)
    this.updateImpacts(delta)

    for (let i = this.enemies.length - 1; i >= 0; i--) {
      if (!this.enemies[i].alive || this.enemies[i].follower.complete) this.enemies.splice(i, 1)
    }
  }

  private updateTowerCombat(tower: HoneyTowerV2View, delta: number): void {
    const runtime = this.towerCombat.get(tower)
    if (!runtime || !tower.canThrow()) return

    for (const event of tower.updateThrow(delta)) {
      if (event.type === 'release' && runtime.pendingTarget) {
        this.launchHoneyProjectile(tower, event.fishIndex, runtime.pendingTarget)
        runtime.pendingTarget = null
      }
    }

    const targetId = selectHoneyTarget(
      { x: tower.x, y: tower.y },
      this.enemies.map(enemy => ({
        id: enemy.id,
        x: enemy.follower.x,
        y: enemy.follower.y,
        progress: enemy.follower.pathIndex,
        alive: enemy.alive,
      })),
      getDemoTowerRange(GAME_WIDTH),
    )
    const target = targetId === null ? null : this.enemies.find(enemy => enemy.id === targetId) ?? null
    const hasFish = runtime.loans.lent.some(lent => !lent)
    const cooldown = advanceHoneyCooldown(
      runtime.cooldown,
      delta,
      Boolean(target && hasFish && !tower.isThrowing()),
    )
    runtime.cooldown = cooldown.state
    if (!cooldown.fired || !target) return

    const borrowed = borrowHoneyFish(runtime.loans)
    runtime.loans = borrowed.state
    if (borrowed.fishIndex === null || !tower.playThrow(
      borrowed.fishIndex,
      target.follower.x,
      target.follower.y,
    )) {
      if (borrowed.fishIndex !== null) runtime.loans = returnHoneyFish(runtime.loans, borrowed.fishIndex)
      runtime.cooldown = createHoneyCooldown()
      return
    }
    runtime.pendingTarget = {
      id: target.id,
      x: target.follower.x,
      y: target.follower.y,
    }
  }

  private launchHoneyProjectile(
    tower: HoneyTowerV2View,
    fishIndex: 0 | 1,
    target: { id: number; x: number; y: number },
  ): void {
    const muzzle = tower.getMuzzleWorld()
    const sprite = this.add.sprite(muzzle.x, muzzle.y, HONEY_V2_ASSETS.fish.key, 0)
      .setScale(HONEY_TOWER_DISPLAY_SCALE)
      .setDepth(Math.round(muzzle.y) + 6)
    this.projectiles.push({
      sprite,
      state: createHoneyProjectile(muzzle, target.id, target, HONEY_COMBAT.projectileSpeed),
      owner: tower,
      fishIndex,
      ageMs: 0,
    })
  }

  private updateProjectiles(delta: number): void {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const projectile = this.projectiles[i]
      const target = this.enemies.find(enemy => enemy.id === projectile.state.targetId && enemy.alive)
      const stepped = stepHoneyProjectile(
        projectile.state,
        delta,
        target ? { x: target.follower.x, y: target.follower.y } : null,
      )
      projectile.state = stepped.state
      projectile.ageMs += delta
      projectile.sprite
        .setPosition(stepped.state.x, stepped.state.y)
        .setFrame(Math.floor(projectile.ageMs / 140) % 4)
        .setFlipX(stepped.state.targetX < stepped.state.x)
        .setDepth(Math.round(stepped.state.y) + 6)
      if (!stepped.hit) continue

      if (target) {
        const hit = applyHoneyHit(target)
        target.hp = hit.hp
        target.alive = hit.alive
        target.slowRemainingMs = hit.slowRemainingMs
        target.sprite.setTint(0xffc04d)
        if (!target.alive) {
          target.sprite.destroy()
          this.setStatus('Sticky fish defeated the snowman. The Gourami returns to the tub.')
        } else {
          this.setStatus(`Sticky hit: snowman ${target.hp}/9 HP and slowed 40%.`)
        }
      }

      const impact = this.add.sprite(stepped.state.x, stepped.state.y, HONEY_V2_ASSETS.impact.key, 0)
        .setScale(HONEY_TOWER_DISPLAY_SCALE)
        .setDepth(Math.round(stepped.state.y) + 8)
      this.impacts.push({
        sprite: impact,
        owner: projectile.owner,
        fishIndex: projectile.fishIndex,
        ageMs: 0,
      })
      projectile.sprite.destroy()
      this.projectiles.splice(i, 1)
    }
  }

  private updateImpacts(delta: number): void {
    for (let i = this.impacts.length - 1; i >= 0; i--) {
      const impact = this.impacts[i]
      impact.ageMs += delta
      impact.sprite.setFrame(Math.min(2, Math.floor(impact.ageMs / 80)))
      if (impact.ageMs < 240) continue

      impact.sprite.destroy()
      const runtime = this.towerCombat.get(impact.owner)
      if (runtime && impact.owner.active) {
        runtime.loans = returnHoneyFish(runtime.loans, impact.fishIndex)
        impact.owner.returnFish(impact.fishIndex)
      }
      this.impacts.splice(i, 1)
    }
  }

  private teardown(): void {
    this.input.off('pointerdown', this.onMapPointerDown, this)
    this.input.off('pointermove', this.onMapPointerMove, this)
    this.input.keyboard?.off('keydown-ESC', this.onPlacementEscape, this)
    this.load.off('loaderror', this.handleLoadError, this)
    this.destroyPlacementGhost()
    this.placement = createDemoPlacementState()
    for (const tower of this.towers) tower.destroy()
    for (const enemy of this.enemies) enemy.sprite.destroy()
    for (const projectile of this.projectiles) projectile.sprite.destroy()
    for (const impact of this.impacts) impact.sprite.destroy()
    this.towers.length = 0
    this.enemies.length = 0
    this.projectiles.length = 0
    this.impacts.length = 0
    this.towerCombat.clear()
    this.selectedTower = null
  }
}
